import React, { useState } from 'react';
import { Alert, Box, Typography, IconButton, Collapse } from '@mui/material';
import Schedule from '@mui/icons-material/Schedule';
import Close from '@mui/icons-material/Close';
import { useDashboardBootstrap } from '../../../hooks/useDashboardBootstrap';
import { todayLocalDateString } from '../../../utils/dateUtils';

/**
 * Banner that displays DST (Daylight Saving Time) reminders.
 *
 * Shows a reminder the day before a DST transition and on the day
 * of the transition itself so practitioners can verify appointments.
 *
 * Dismissal is stored in sessionStorage so the banner stays hidden
 * for the current browser session but reappears on next login.
 */
const DSTBanner: React.FC = () => {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    const stored = sessionStorage.getItem('dstBannerDismissed');
    if (!stored) return false;
    try {
      const parsed = JSON.parse(stored);
      // Only keep dismissal if it was for the same date (re-show on new transition)
      return parsed.date === todayLocalDateString();
    } catch {
      return false;
    }
  });

  // DST status is part of the shared dashboard bootstrap bundle so the
  // banner never fires its own request — we just read whatever the
  // Layout already fetched.
  const { data: bootstrap } = useDashboardBootstrap();
  const status = bootstrap?.dst ?? null;

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem(
      'dstBannerDismissed',
      JSON.stringify({ date: todayLocalDateString() })
    );
  };

  if (dismissed || !status?.show_reminder) {
    return null;
  }

  const isDayBefore = status.reminder_type === 'day_before';
  const severity = isDayBefore ? 'info' : 'warning';
  const bgVar = isDayBefore ? 'var(--dst-info-bg)' : 'var(--dst-warning-bg)';
  const borderVar = isDayBefore ? 'var(--dst-info-border)' : 'var(--dst-warning-border)';

  return (
    <Collapse in={true}>
      <Alert
        severity={severity}
        icon={<Schedule />}
        sx={{
          borderRadius: 0,
          backgroundColor: bgVar,
          borderBottom: `1px solid ${borderVar}`,
          py: 0.5,
          px: 1,
          '& .MuiAlert-message': {
            width: '100%',
          },
        }}
        action={
          <IconButton
            size="small"
            onClick={handleDismiss}
            aria-label="Dismiss DST reminder"
            sx={{ color: borderVar }}
          >
            <Close fontSize="small" />
          </IconButton>
        }
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {status.transition?.label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {status.message}
          </Typography>
        </Box>
      </Alert>
    </Collapse>
  );
};

export default DSTBanner;
